import React, {useState, useEffect} from 'react'
import styled from 'styled-components'

import HideWrap from '../../../components/assessmentReview/comp/hideWrap'
import HealthCard from '../../../components/healthScore/card'
import Information from '../../../components/UI/extraInformation'
import { SectionStyle } from '../index.styled'
import { ScoreColorGroup } from '../components/Widgets'
import { RangeBar } from '../../../components/assessmentReview/comp/bodyComp'
import AsymmetryChart from '../components/AsymmetryChart'
import CervicalLateralFlexionImage from '../../../asserts/images/physical-assessment/ic-cervical-lateral-flexion.svg'
import MultiSegmentalFlexionImage from '../../../asserts/images/physical-assessment/ic-multi-segmental-flexion.png'
import MultiSegmentalFlexionMale from '../../../asserts/images/physical-assessment/MultisegmentalFlexionMale.svg'
import InternalExternalImage from '../../../asserts/images/physical-assessment/ic-internal-external-range.svg'
import InternalExternalImageFemale from '../../../asserts/images/physical-assessment/SierFemale.svg'
import SingleLegSquatImage from '../../../asserts/images/physical-assessment/ic-single-leg-squat.svg'
import OverheadSquatImage from '../../../asserts/images/physical-assessment/ic-overhead-squat.svg'
import OverheadSquatFemale from '../../../asserts/images/physical-assessment/OverheadSquatfemale.svg'
import { getMetricById } from '../../../api'
import { formatDisplayName, getRangeByAgeAndGender } from './LowlevelMetricsHelpers'
import { getRangeAgeScopes } from '../../../api'
import LowLevelMetrics from '../../../components/assessmentReview/comp/LowlevelMetrics'
import { RangeColors } from '../../../utils'
import { Divider } from 'antd'
import ConditionalHideWrap from '../../../components/assessmentReview/comp/ConditionalHidewrap'

const Container = styled.section`
  ${SectionStyle}
`

export default function MovementQualitySection(props) {
  const {
    hideSectionHandle,
    hideSection,
    dataList,
    compareData,
    save,
    loginRole,
    onScoreChangeHandle,
    arDetail: { notes, status },
    view,
    cancelEdit,
    handleCommendSave,
    updateEditingCount,
    person,
    ageScopes,
    data: {
      cervical_lateral_flexion_asymmetry,
      cervical_lateral_flexion_left,
      cervical_lateral_flexion_right,
      multi_segment_flexion,
      peak_knee_flexion_asymmetry,
      peak_knee_flexion_left,
      peak_knee_flexion_right,
      sier_asymmetry,
      sier_left,
      sier_right,
      single_leg_squat_depth_asymmetry,
      single_leg_squat_depth_left,
      single_leg_squat_depth_right
    }
  } = props

  const [valueResults, setValueResults] = useState([])

  useEffect(() => {
    fetchBodyCompMetricsDetails()
  }, [])

  const fetchBodyCompMetricsDetails = async () => {
    const metricsWithSections = [
      'cervical_lateral_flexion_left',
      'cervical_lateral_flexion_right',
      'multi_segment_flexion',
      'sier_right',
      'sier_left',
      'single_leg_squat_depth_right',
      'single_leg_squat_depth_left',
      'peak_knee_flexion_right',
      'peak_knee_flexion_left'
    ]

    const ageScopes = (await getRangeAgeScopes()).age_scope

    const biomarkerDetailsPromises = metricsWithSections.map(async (metricKey) => {
      const metric = props.data[metricKey]

      if (!metric) {
        console.warn(`No data found for metric: ${metricKey}`)
        return null
      }

      if (['orange', 'red', 'yellow'].includes(metric.color)) {
        const conventionalRanges = getRangeByAgeAndGender(metric.ranges, person, ageScopes)

        if (metric.metricId) {
          const biomarkerDetail = await getMetricById(metric.metricId, null, 'physical_assessments')

          const introduction = (biomarkerDetail && biomarkerDetail.physical_assessment && biomarkerDetail.physical_assessment.introduction)
            ? biomarkerDetail.physical_assessment.introduction
            : 'No introduction available'

          return {
            displayName: formatDisplayName(metricKey),
            value: metric.value,
            color: metric.color,
            level: metric.level,
            conventionalRanges,
            biomarkerDetail,
            introduction,
            sectionName: 'Movement Quality'
          }
        }
      }

      return null
    })

    const results = await Promise.all(biomarkerDetailsPromises)
    const filteredResults = results.filter(result => result !== null)

    setValueResults(filteredResults)
  }

  const data = dataList.find((item) => item.name === 'Movement Quality') || {}
  const compare =
    compareData.find((item) => item.name === 'Movement Quality') || {}

  const movementQuality = notes.find(
    (item) => item.pillar === 'movement quality'
  )
  const gender = person && person.profile && person.profile.gender

  const metrics = [
    {
      name: 'Cervical Lateral Flexion',
      image: CervicalLateralFlexionImage,
      bars: [
        {
          ...cervical_lateral_flexion_left,
          name: 'Cervical Lateral Flexion Left'
        },
        {
          ...cervical_lateral_flexion_right,
          name: 'Cervical Lateral Flexion Right'
        }
      ],
      asymmetry: {
        ...cervical_lateral_flexion_asymmetry
      },
      asymmetryText: 'Cervical Lateral Flexion Asymmetry'
    },
    {
      name: 'Multi Segmental Flexion',
      image: gender === 'male' ? MultiSegmentalFlexionMale : MultiSegmentalFlexionImage,
      imageStyle: {
        width: 180
      },
      bars: [
        {
          ...multi_segment_flexion,
          name: 'Multi Segmental Flexion'
        }
      ]
    },
    {
      name: 'Internal and External Range of Motion',
      image: gender === 'male' ? InternalExternalImage : InternalExternalImageFemale,
      imageStyle: {
        width: 180
      },
      bars: [
        {
          ...sier_left,
          name: 'Shoulder Internal External Range of Motion Left'
        },
        {
          ...sier_right,
          name: 'Shoulder Internal External Range of Motion Right'
        }
      ],
      asymmetry: {
        ...sier_asymmetry
      },
      asymmetryText: 'Shoulder Internal External Range of Motion Asymmetry'
    },
    {
      name: 'Single Leg Squat',
      image: SingleLegSquatImage,
      bars: [
        {
          ...single_leg_squat_depth_left,
          name: 'Single Leg Squat Depth Left'
        },
        {
          ...single_leg_squat_depth_right,
          name: 'Single Leg Squat Depth Right '
        }
      ],
      asymmetry: {
        ...single_leg_squat_depth_asymmetry
      },
      asymmetryText: 'Single Leg Squat Depth Asymmetry'
    },
    {
      name: 'Overhead Squat',
      image: gender === 'male' ? OverheadSquatImage : OverheadSquatFemale,
      imageStyle: {
        width: 180
      },
      bars: [
        {
          ...peak_knee_flexion_left,
          name: 'Peak Knee Flexion left'
        },
        {
          ...peak_knee_flexion_right,
          name: 'Peak Knee Flexion Right'
        }
      ],
      asymmetry: {
        ...peak_knee_flexion_asymmetry
      },
      asymmetryText: 'Peak Knee Flexion Asymmetry'
    }
  ]

  return (
    <>
      <ConditionalHideWrap
        name="Movement Quality"
        isSection={true}
        syncStatus={hideSectionHandle}
        status={hideSection}
        view={view}
        condition={
          view === 'Draft' || (view !== 'Draft' && !hideSection.includes('Movement Quality'))
        }
      >
        <Container>
          <h1 className="ar-section-title">Movement Quality</h1>
          <h3 className="ar-section-card-title">Summary</h3>
          <HealthCard
            {...data}
            compare={compare}
            save={save}
            loginRole={loginRole}
            status={view === 'Draft' ? 'Draft' : 'Opened'}
            onChangeHandle={onScoreChangeHandle}
            disEdit={status === 1 || view === 'Preview'}
            cancel={cancelEdit}
          />
          <Information
            title="COMMENT/NOTE"
            disMarkDone={true}
            initialValue={movementQuality ? movementQuality.memo : ''}
            status={view === 'Draft' ? 'Draft' : 'Opened'}
            updateCommend={(value, status) =>
              handleCommendSave(value, 'movement quality', status)
            }
            updateEditingCount={updateEditingCount}
          />
          <ScoreColorGroup domain={'movement'} />
          {metrics.map((metric, index) => {
            return (
              <React.Fragment key={index}>
                {index > 0 && <Divider />}
                <MovementMetricCard
                  {...metric}
                  {...{
                    person,
                    ageScopes
                  }}
                />
              </React.Fragment>
            )
          })}
          <LowLevelMetrics metricValues={valueResults} hideSection={hideSection} view={view} hideSectionHandle={hideSectionHandle} />
        </Container>
      </ConditionalHideWrap>
    </>
  )
}

const MetricContainer = styled.div`
  height: ${(props) => props.height || '500px'};
  position: relative;

  .metric-name {
    font-size: 18px;
    letter-spacing: 0.3px;
    line-height: 27px;
    position: absolute;
    top: ${(props) => props.nameMarginTop || '68px'};
    color: #4c6072;
  }
  .metric-content {
    display: flex;
    align-items: center;
    height: 100%;
    & > div {
      display: inline-block;
    }

    .metric-middle-container {
      display: flex;
      flex-direction: column;
    }
  }
  .metric-polar-chart-container {
    position: absolute;
    width: 340px;
    top: 50%;
    right: -75px;
    transform: translateY(-50%);
    .polar-chart-name {
      font-size: 15px;
      line-height: 21px;
      color: #4c6072;
      display: flex;
      text-align: center;
      justify-content: center;
      & > div {
        max-width: 220px;
      }
    }
  }
`

export function MovementMetricCard(props) {
  const { bars, person, ageScopes } = props
  return (
    <MetricContainer height={props.height} nameMarginTop={props.nameMarginTop}>
      <div className="metric-name">{props.name}</div>
      <div className="metric-content">
        <div
          className="metric-image-conatiner"
          style={{
            width: '22.57%'
          }}
        >
          <img src={props.image} alt="bg" style={props.imageStyle || {}} />
        </div>
        <div
          className="metric-middle-container"
          style={{
            width: '53.57%'
          }}
        >
          {bars.map((bar, index) => {
            return (
              <RangeBar
                key={index}
                {...bar}
                {...{ person, ageScopes }}
                levelStyle={{
                  padding: '5px 13px',
                  color: 'white',
                  backgroundColor: RangeColors[bar.color] || 'transparent',
                  fontWeight: '600',
                  borderRadius: '6px'
                }}
                showRangeArea={true}
                hideName={false}
                hideValueNumber={false}
                showLevelOnNameRight={true}
              />
            )
          })}
        </div>
      </div>
      {props.asymmetry && (
        <div className="metric-polar-chart-container">
          <div className="polar-chart-name">
            <div>{props.asymmetryText}</div>
          </div>
          <AsymmetryChart metric={props.asymmetry} {...{ ageScopes, person }} />
        </div>
      )}
    </MetricContainer>
  )
}
